import React, { type ReactNode } from "react";
import FocusTrap from "focus-trap-react";
import * as Styles from "../../../styles/v2/toyota/globals/Modal";
import type { BreakPointValuesType } from "../../../themes/common";
import useModalBaseView from "../../../hooks/useModalBaseView";
import { MODAL_DIALOG_ID } from "../../../utils";
import { useCommonSelector } from "../../../redux/commonStore";
import type { ModalIdsType } from "../../../types/CommonTypes";

type ModalBaseViewType = {
    modalIdType: ModalIdsType;
    onClose: () => void;
    onTransitionEnd?: () => void;
    show: boolean;
    children: ReactNode;
    size?: BreakPointValuesType;
    contentClass?: string;
    disableOutsideClick?: boolean;
    isFullscreen?: boolean;
    fullWidth?: boolean;
    hasFadeInAnimation?: boolean;
    focusTrap?: boolean;
    onAnimationEnd?: () => void;
};

/**
 * This wrapper should be used for every Modal component
 * It contains required modal wrappers and the overlay background.
 */
const ModalBaseView = (props: ModalBaseViewType): JSX.Element => {
    const { modalIdType, show, children, size, fullWidth, isFullscreen } = props;
    const { onTransitionEnd, contentClass, disableOutsideClick, hasFadeInAnimation, focusTrap = true } = props;
    const { onClose, onAnimationEnd } = props;
    const modalRef = useModalBaseView({ onClose, disableOutsideClick, show, modalIdType });

    // AEM Author - modals should not cover other components and should be top aligned so editors see them immediately
    const isAuthor = useCommonSelector((state) => state.commonSettings?.query?.authorPreview || false);

    return (
        <FocusTrap
            focusTrapOptions={{
                tabbableOptions: { getShadowRoot: () => false },
                allowOutsideClick: true,
            }}
            active={focusTrap}
        >
            <Styles.Wrapper role="dialog" isActive={show} onTransitionEnd={onTransitionEnd} isAuthor={isAuthor}>
                <Styles.Dialog
                    id={MODAL_DIALOG_ID}
                    data-cpdqm="ignore"
                    ref={modalRef}
                    isActive={show}
                    hasFadeInAnimation={hasFadeInAnimation}
                    size={size}
                    isFullscreen={isFullscreen}
                    fullWidth={fullWidth}
                    isAuthor={isAuthor}
                    onAnimationEnd={onAnimationEnd}
                >
                    <Styles.Content className={contentClass}>{children}</Styles.Content>
                </Styles.Dialog>
            </Styles.Wrapper>
        </FocusTrap>
    );
};

export default ModalBaseView;
